// Stillhouse view — Roles & permissions.
//
// The matrix is the REAL permission catalog: one row per key in
// apps/api/src/plugins/auth.ts PermissionKey, with the grants the role presets
// are seeded with (apps/api/src/scripts/seed.ts ROLE_DEFS). The design's
// original 11 invented capabilities are gone — a matrix that advertises
// capabilities the API has no key for is a promise nothing enforces.
import React from "react";
import { C, F, Btn } from "../brand/apo";
import { Card, Chip, PageHeader, COPPER } from "../brand/sh";
import type { PermissionKey } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

export function Roles() {
  const roles = ["Owner", "Admin", "Team lead", "Member", "Contractor"];
  // [label, permission key, grants for Owner / Admin / Team lead / Member /
  // Contractor] — mirrors PERMISSION_DEFS + ROLE_DEFS in seed.ts.
  const perms: [string, PermissionKey, number[]][] = [
    ["View machines, sessions & mesh", "fleet.read", [1, 1, 1, 1, 1]],
    ["Enroll, assign & unenroll machines", "fleet.manage", [1, 1, 1, 0, 0]],
    ["Force-disconnect live sessions", "session.disconnect", [1, 1, 1, 0, 0]],
    ["Mint enrollment tokens", "enroll.mint", [1, 1, 1, 0, 0]],
    ["Switch mesh mode & policies", "mesh.manage", [1, 1, 0, 0, 0]],
    ["Manage users & roles", "users.manage", [1, 1, 0, 0, 0]],
    ["Manage org settings", "settings.manage", [1, 1, 0, 0, 0]],
    ["Read & export the audit log", "audit.read", [1, 1, 1, 0, 0]],
  ];
  const cell = (v: number) => (v ? <span style={{ color: C.glow, fontWeight: 700 }}>●</span> : <span style={{ color: C.fgFaint }}>–</span>);
  // Preset roles — capability documentation, not a live user tally.
  const roleCards: [string, string, string][] = [
    ["Owner", "Full control", C.amber],
    ["Admin", "Manage fleet & people", C.glow],
    ["Team lead", "Manage own team", C.lilac],
    ["Member", "Use assigned machines", C.fgMute],
    ["Contractor", "Time-boxed access", COPPER],
  ];
  return (
    <React.Fragment>
      <PageHeader
        crumb="PEOPLE › ROLES"
        title="Roles & permissions"
        sub="Granular role-based access control. Every action in the stillhouse maps to a permission; roles bundle them and inherit down the org."
      >
        <Btn ghost>Duplicate role</Btn>
        <Btn primary>New custom role</Btn>
      </PageHeader>
      <Scroll>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14 }}>
          {roleCards.map((r, i) => (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.line}`, borderTop: `2px solid ${r[2]}`, padding: "14px 16px" }}>
              <div style={{ fontFamily: F.display, fontWeight: 500, fontSize: 17, color: C.fgDark }}>{r[0]}</div>
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint, marginTop: 4 }}>{r[1]}</div>
            </div>
          ))}
        </div>
        <Card title="Permission matrix" pad={false} tag={<Chip color={C.fgMute}>{perms.length} permission keys</Chip>}>
          <div style={{ display: "grid", gridTemplateColumns: `2fr repeat(${roles.length}, 1fr)`, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.12em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
            <span>CAPABILITY</span>
            {roles.map((r) => (
              <span key={r} style={{ textAlign: "center" }}>
                {r.toUpperCase()}
              </span>
            ))}
          </div>
          {perms.map((p, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: `2fr repeat(${roles.length}, 1fr)`, padding: "11px 18px", borderBottom: i < perms.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13, color: C.fgDark }}>
              <span style={{ minWidth: 0 }}>
                {p[0]}
                <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint, marginLeft: 8 }}>{p[1]}</span>
              </span>
              {p[2].map((v, j) => (
                <span key={j} style={{ textAlign: "center", fontSize: 15 }}>
                  {cell(v)}
                </span>
              ))}
            </div>
          ))}
        </Card>
      </Scroll>
    </React.Fragment>
  );
}
